<template>
  <div class="class-card">
    <header class="class-card__header">
      <h3 class="class-card__title">
        <router-link
          :to="cardRoute"
          class="class-card__title-link"
          @click.prevent="navigateToClass"
        >
          {{ displayTitle }}
        </router-link>
      </h3>
        <div class="class-card__header-actions">
          <span :class="['class-card__badge', availabilityStatusClass]">
            <span class="class-card__badge-dot"></span>
            {{ availabilityLabel }}
          </span>
          <BaseButton
            v-if="showCollapse"
            class="class-card__header-button"
            type="button"
            :aria-label="toggleAriaLabel"
            :aria-expanded="!isCollapsed"
            @click="toggleCollapse"
          >
            <IconChevronUp
              :size="16"
              :svg-class="['class-card__header-icon', { 'class-card__header-icon--collapsed': isCollapsed }]"
            />
          </BaseButton>
        </div>
    </header>
    <template v-if="!isCollapsed">
      <div class="class-card__therapist" v-if="classData.therapist?.name">
        <router-link
          v-if="therapistProfileRoute"
          :to="therapistProfileRoute"
          class="class-card__therapist-link"
        >
          <DoctorImage
            :image="therapistImage"
            :name="classData.therapist.name"
            size="3.2rem"
          />
          <span>{{ classData.therapist.name }}</span>
        </router-link>
        <template v-else>
          <DoctorImage
            :image="therapistImage"
            :name="classData.therapist.name"
            size="3.2rem"
          />
          <span>{{ classData.therapist.name }}</span>
        </template>
      </div>

      <ul class="class-card__info">
        <li
          v-if="centerAddress"
          class="class-card__info-item"
        >
          <span class="class-card__icon">
            <img
              src="/img/map_pin.18048e0c.svg"
              :alt="$t('Class location icon alt')"
              class="w-[1.6rem]"
            />
          </span>
          <span>{{ centerAddress }}</span>
        </li>
        <li class="class-card__info-item class-card__info-item--date">
          <span class="class-card__icon">
            <IconCalendar :size="12" />
          </span>
          <span>{{ calendarDisplay }}</span>
        </li>
        <li v-if="showTimeRow" class="class-card__info-item">
          <span>{{ timeDisplay }}</span>
        </li>
        <li class="class-card__info-item">
        <span class="class-card__icon">
          <IconDuration :size="12" />
        </span>
          <span>{{ durationDisplay }}</span>
        </li>
        <li class="class-card__info-item">
        <span class="class-card__icon">
          <IconParticipants :size="12" />
        </span>
          <span>{{ participantsDisplay }}</span>
        </li>
      </ul>

      <div class="class-card__divider" role="presentation"></div>

      <div class="class-card__price-row">
        <span class="class-card__price-label">{{ $t('Price') }}:</span>
        <span class="class-card__price-amount">{{ priceDisplay }}</span>
      </div>

      <div class="class-card__cta">
        <slot
          name="cta"
          :class-data="classData"
          :navigate="navigateToClass"
          :card-route="cardRoute"
        >
          <AppButton
            :label="$t('More information')"
            variant="secondary"
            class="class-card__more-button"
            :block="true"
            @click="navigateToClass"
          />
        </slot>
      </div>
    </template>
  </div>
</template>

<script>
import classCardConfig from './ClassCard.logic';

export default classCardConfig;
</script>


<style scoped>
.class-card {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  background: rgba(183, 160, 218, 0.15);
  border-radius: 20px;
  padding: 1.6rem 1.4rem;
  border: 1px solid rgba(45, 34, 68, 0.1);
}

.class-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.2rem;
}

.class-card__title {
  margin: 0;
  font-size: 1.6rem;
  font-weight: 700;
  color: #2d2244;
}

.class-card__title-link {
  color: inherit;
  text-decoration: none;
}

.class-card__title-link:hover {
  text-decoration: underline;
}

.class-card__header-actions {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.class-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 0.8rem;
  border-radius: 60px;
  background: #ffffff;
  font-size: 1.1rem;
  font-weight: 500;
  color: #2d2244;
}

.class-card__badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}

.class-card__badge--available {
  color: #00c666;
}

.class-card__badge--full {
  color: #d7263d;
  background: rgba(215, 38, 61, 0.12);
}

.class-card__header-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #b7a0da;
  color: #ffffff;
  text-decoration: none;
  transition: background 0.2s ease;
}

.class-card__header-button:hover {
  background: #9f84ce;
}

.class-card__therapist {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #2d2244;
  font-size: 1.2rem;
  font-weight: 500;
}

.class-card__therapist-link {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #2d2244;
  font-size: 1.2rem;
  font-weight: 500;
  text-decoration: none;
}

.class-card__therapist-link:hover {
  color: #000;
}


.class-card__info {
  display: flex;
  flex-wrap: wrap;
  gap: 1.2rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.class-card__info-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: #2d2244;
  font-size: 1.2rem;
  font-weight: 500;
}

.class-card__info-item--date {
  gap: 0.4rem;
}

.class-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(183, 160, 218, 0.25);
  color: #2d2244;
}

.class-card__icon :deep(svg) {
  display: block;
}

.class-card__divider {
  border-top: 1px dashed rgba(45, 34, 68, 0.15);
  margin: 0;
}

.class-card__price-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.8rem;
  color: #2d2244;
}

.class-card__price-label {
  font-size: 1.2rem;
  font-weight: 700;
}

.class-card__price-amount {
  font-size: 1.8rem;
  font-weight: 600;
}

.class-card__header-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  border-radius: 60px;
  background: #000000;
  color: #ffffff;
  font-size: 1.3rem;
  font-weight: 600;
  border: 1px solid transparent;
  transition: background 0.2s ease, color 0.2s ease;
}

.class-card__header-button {
  width: 36px;
  padding: 0;
}

.class-card__header-icon {
  transition: transform 0.2s ease;
}

.class-card__header-icon--collapsed {
  transform: rotate(180deg);
}

.class-card__header-button:hover {
  background: #262626;
}

.class-card__cta {
  margin-top: 1.6rem;
}

.class-card__cta :deep(.app-button) {
  width: 100%;
}

.class-card__cta :deep(.class-card__more-button) {
  border-color: #000000;
  background: #ffffff;
  color: #000000;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.class-card__cta :deep(.class-card__more-button:not(:disabled):not(.app-button--loading):hover) {
  background: #f2f3f5;
  color: #111827;
  border-color: #000000;
  box-shadow: 0 8px 20px rgba(17, 24, 39, 0.08);
}
</style>
